import { useEffect, useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '@/i18n/LanguageContext';
import { getT } from '@/i18n/translations';

const COLS = 14;
const ROWS = 6;
const TOTAL = COLS * ROWS;
const ACTIVE_COUNT = 4;          // how many dots glow at once
const TICK_MS = 280;              // refresh cadence

/**
 * Pulse Grid — subtle RNG visualization.
 * Renders a 14×6 lattice of gold dots; a few dots light up randomly each tick
 * to suggest a live cryptographically-secure random number generator scanning tickets.
 */
const PulseGrid = () => {
  const { language } = useLanguage();
  const t = getT('pulseGrid', language) || {};
  const label = t.label || 'RANDOMNESS · LIVE';
  const tooltip = t.tooltip || 'Draws are powered by secrets.SystemRandom() — the same CSPRNG class used by banking systems.';

  const dots = useMemo(() => Array.from({ length: TOTAL }, (_, i) => i), []);
  const [active, setActive] = useState(() => pickActive());

  useEffect(() => {
    const id = setInterval(() => setActive(pickActive()), TICK_MS);
    return () => clearInterval(id);
  }, []);

  return (
    <div
      className="flex flex-col items-center gap-2 select-none group"
      data-testid="pulse-grid"
      title={tooltip}
      aria-label={label}
    >
      <div
        className="grid gap-[6px]"
        style={{ gridTemplateColumns: `repeat(${COLS}, minmax(0, 1fr))` }}
        data-testid="pulse-grid-dots"
      >
        {dots.map((i) => {
          const isOn = active.has(i);
          return (
            <motion.span
              key={i}
              className="h-[3px] w-[3px] rounded-full"
              animate={{
                backgroundColor: isOn ? '#e6be4b' : '#cba135',
                opacity: isOn ? 1 : 0.18,
                scale: isOn ? 1.6 : 1,
                boxShadow: isOn
                  ? '0 0 8px rgba(230, 190, 75, 0.85), 0 0 14px rgba(203, 161, 53, 0.45)'
                  : '0 0 0 rgba(0,0,0,0)',
              }}
              transition={{ duration: 0.32, ease: 'easeOut' }}
            />
          );
        })}
      </div>

      <div className="flex items-center gap-2 mt-1">
        <motion.span
          className="h-[6px] w-[6px] rounded-full bg-[#e6be4b]"
          animate={{ opacity: [0.35, 1, 0.35] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
        />
        <span className="text-[10px] tracking-[0.28em] uppercase text-[#cba135]/70 font-medium">
          {label}
        </span>
      </div>
    </div>
  );
};

function pickActive() {
  const set = new Set();
  while (set.size < ACTIVE_COUNT) {
    set.add(Math.floor(Math.random() * TOTAL));
  }
  return set;
}

export default PulseGrid;
